import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchPublicPost } from '../api/socialActions';
import PostCard from '../components/PostCard';
import { useAuth } from '../context/AuthContext';
import { AlertTriangle, ArrowLeft, LogIn } from 'lucide-react';
import { Button } from '../components/ui/button';

/**
 * Phase 50 · Public post detail page.
 *
 * Route: /post/:postId
 *
 * Works for both anonymous and authenticated visitors:
 *  - anonymous → paywalled posts render locked with a login CTA;
 *  - authenticated → the standard PostCard renders (paywall/comments
 *    just like on the feed).
 *
 * Uses the `/api/posts/:id/public` endpoint that returns a light,
 * share-safe payload (paid content stays locked).
 */
const PostDetailPage = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const data = await fetchPublicPost(postId);
        if (alive) setPost(data);
      } catch (err) {
        if (alive) {
          const status = err?.response?.status;
          if (status === 404) setError('not_found');
          else setError('unknown');
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [postId]);

  return (
    <div className="mx-auto max-w-[560px] px-3 py-6" data-testid="post-detail-page">
      <button
        onClick={() => navigate(-1)}
        className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        data-testid="post-detail-back-btn"
      >
        <ArrowLeft className="h-4 w-4" /> Torna indietro
      </button>

      {loading && (
        <div className="flex justify-center py-14">
          <div className="h-7 w-7 animate-spin rounded-full border-2 border-border border-t-amber-400" />
        </div>
      )}

      {!loading && error === 'not_found' && (
        <div className="rounded-lg border border-border bg-card px-6 py-14 text-center" data-testid="post-detail-not-found">
          <AlertTriangle className="mx-auto mb-3 h-12 w-12 text-amber-500" strokeWidth={1.4} />
          <h2 className="text-lg font-semibold">Post non disponibile</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Il post potrebbe essere stato rimosso o non è più accessibile.
          </p>
          <Button onClick={() => navigate('/')} className="mt-4 gold-gradient">
            Vai al feed
          </Button>
        </div>
      )}

      {!loading && error === 'unknown' && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-6 py-10 text-center">
          <p className="text-sm text-red-400">Errore durante il caricamento del post.</p>
        </div>
      )}

      {!loading && !error && post && (
        <>
          <PostCard post={post} onUpdated={setPost} onHidden={() => navigate('/')} />
          {!user && (
            <div className="mt-2 rounded-lg border border-amber-500/30 bg-gradient-to-r from-amber-900/10 to-neutral-900 p-4 text-center" data-testid="post-detail-login-cta">
              <p className="text-sm">
                <span className="font-semibold">Vuoi vedere di più?</span>{' '}
                <span className="text-muted-foreground">Registrati o accedi per interagire, salvare e accedere ai contenuti.</span>
              </p>
              <div className="mt-3 flex justify-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => navigate('/register')}
                  data-testid="post-detail-register-btn"
                >
                  Registrati
                </Button>
                <Button
                  onClick={() => navigate(`/login?next=/post/${postId}`)}
                  className="gold-gradient"
                  data-testid="post-detail-login-btn"
                >
                  <LogIn className="mr-2 h-4 w-4" /> Accedi
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PostDetailPage;
